import React, { useEffect, useRef } from "react";
import {
  Container,
  Grid,
  Box,
  TextField,
  Typography,
  InputAdornment,
  useTheme,
  useMediaQuery,
  RadioGroup,
  FormControlLabel,
  Radio,
  Autocomplete,
} from "@mui/material";
import { styled } from "@mui/system";

const genderArray = ["Male", "Female", "Other"];

// Distant Vision options
const distantVisionOptions = ["6/6", "6/9", "6/12", "6/18", "6/24", "6/36", "6/60", "5/60", "4/60", "3/60", "2/60", "1/60", "FCCF", "CF", "HMCF", "HM", "PL+", "PL-", "NPL"];

// Near Vision options
const nearVisionOptions = ["N5", "N6", "N8", "N10", "N12", "N14", "N18", "N24", "N36"];

const StyledContainer = styled(Container)(({ theme }) => ({
  padding: theme.spacing(2),
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(1),
    marginLeft: theme.spacing(2), // Added left margin for mobile
    paddingBottom: "80px", // Add bottom padding to account for fixed button
    display: 'flex',
    flexDirection: 'column',
  },
}));

const StyledGridItem = styled(Grid)(({ theme }) => ({
  display: "flex",
  alignItems: "flex-start", // Align all items from the start (top-aligned)
  marginBottom: theme.spacing(2),
  flexWrap: "nowrap", // Prevent wrapping to maintain alignment
  [theme.breakpoints.down("sm")]: {
    flexDirection: "column", // Stack items vertically on mobile
    flexWrap: "wrap",
  },
  [theme.breakpoints.between("sm", "md")]: {
    flexDirection: "row", // Keep row layout for tablet
    alignItems: "flex-start",
    flexWrap: "nowrap",
  },
}));

const StyledLabel = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(1),
  minWidth: "200px", // Minimum width for the label
  width: "200px", // Fixed width for the label
  textAlign: "left",
  wordBreak: "break-word", // Ensure long words break to new lines
  flexShrink: 0, // Prevent label from shrinking
  [theme.breakpoints.down("sm")]: {
    width: "auto", // No fixed width on mobile
    minWidth: "auto",
    marginBottom: theme.spacing(1),
  },
  [theme.breakpoints.between("sm", "md")]: {
    minWidth: "180px", // Slightly smaller for tablet
    width: "180px",
  },
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  width: "300px", // Fixed width for text field
  [theme.breakpoints.down("sm")]: {
    width: "100%", // Full width on mobile
  },
}));

const StyledRadioGroup = styled(RadioGroup)(({ theme }) => ({
  display: "flex",
  flexDirection: "row",
  justifyContent: "space-between",
  [theme.breakpoints.down("sm")]: {
    flexDirection: "row", // Ensure radio buttons are in a row on mobile
    justifyContent: "flex-start", // Align them properly on mobile
    flexWrap: "nowrap", // Avoid wrapping the radio buttons on mobile
  },
}));

const StyledFormControlLabel = styled(FormControlLabel)(({ theme, checked }) => ({
  border: checked ? "2px solid #0D1E83" : "1px solid rgba(0, 0, 0, 0.2)", // Conditional border
  width: "auto", // Let the width adjust naturally
  margin: theme.spacing(0.5),
  borderRadius: "4px",
  padding: "4px 12px",
  whiteSpace: "nowrap", // Prevent label from wrapping
  display: "inline-block",
  [theme.breakpoints.down("sm")]: {
    padding: "8px 16px", // Increased padding:  Top/Bottom and Left/Right
    margin: "4px 4px",  // Slightly increased horizontal margin
  },
}));

const StyledRadio = styled(Radio)(({ theme }) => ({
  "&.Mui-checked": {
    color: "#0D1E83", // Change the circle color when selected
  },
  "&.MuiRadio-root": {
    borderRadius: "50%", // Ensure radio button is round
  },
  [theme.breakpoints.down("sm")]: {
    width: "24px", // Increased size of radio button on mobile
    height: "24px",
  },
}));

const NewPatientForm = ({ formik, findPatient = false, handleFindPatient, isFollowUp, setIdValue }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const formContainerRef = useRef(null);
  const pendingSearchTimeoutRef = useRef(null);

  // Filter options for distant vision based on input
  const filterDistantVisionOptions = (options, { inputValue }) => {
    if (!inputValue) return options;
    const lowerInput = inputValue.toLowerCase();
    // If input starts with number, show only numeric options that contain the typed value
    if (/^\d/.test(inputValue)) {
      return options.filter(option => 
        option.match(/^\d/) && option.includes(inputValue)
      );
    }
    // If input contains letters, show only text options that match
    if (/[a-zA-Z]/.test(inputValue)) {
      return options.filter(option => 
        !option.match(/^\d/) && option.toLowerCase().includes(lowerInput)
      );
    }
    return options;
  };

  // Filter options for near vision - only show if starts with N
  const filterNearVisionOptions = (options, { inputValue }) => {
    if (!inputValue) return options;
    const upperInput = inputValue.toUpperCase();
    // Must start with N
    if (!upperInput.startsWith('N')) {
      return [];
    }
    // Filter by number after N
    const numberPart = upperInput.substring(1);
    if (numberPart) {
      return options.filter(option => option.toUpperCase().includes(upperInput));
    }
    return options;
  };

  const searchPatient = () => {
    const id = formik.values.pat_info?.otherId;
    const followUpFlag = localStorage.getItem("is_follow_up_patient");
    
    // Skip search if we already know it's a follow-up patient
    if (followUpFlag === "true" && isFollowUp) {
      return;
    }
    if (id?.trim()) {
      handleFindPatient();
      setIdValue(id)
    }
  };

  // Defer search on blur so that if the user clicks another form control (e.g. gender radio),
  // we cancel the search and the click is not overwritten when the API returns.
  const handlePatientIdBlur = () => {
    if (pendingSearchTimeoutRef.current) {
      clearTimeout(pendingSearchTimeoutRef.current);
      pendingSearchTimeoutRef.current = null;
    }
    pendingSearchTimeoutRef.current = setTimeout(() => {
      pendingSearchTimeoutRef.current = null;
      searchPatient();
    }, 200);
  };

  useEffect(() => {
    const handleMousedown = (event) => {
      const el = event.target;
      const isInsideForm = formContainerRef.current?.contains(el);
      const isInteractive =
        el.closest?.("input, button, [role='radio'], [role='checkbox'], [role='button'], label");
      if (isInsideForm && isInteractive && pendingSearchTimeoutRef.current) {
        clearTimeout(pendingSearchTimeoutRef.current);
        pendingSearchTimeoutRef.current = null;
      }
    };
    document.addEventListener("mousedown", handleMousedown, true);
    return () => {
      document.removeEventListener("mousedown", handleMousedown, true);
    };
  }, []);

  useEffect(() => {
    return () => {
      if (pendingSearchTimeoutRef.current) {
        clearTimeout(pendingSearchTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const handleBeforeUnload = () => {
      localStorage.removeItem("patient_id");
    };
  
    window.addEventListener("beforeunload", handleBeforeUnload);
  
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, []);

  return (
    <StyledContainer ref={formContainerRef}>
      <Grid container spacing={2}>
        {/* Show fields when not searching OR when it's a follow-up patient */}
        {(!findPatient || isFollowUp) && (
          <>
            {/* Patient ID */}
            <StyledGridItem item xs={12}>
              <StyledLabel>Patient ID*</StyledLabel>
              <StyledTextField
                required
                placeholder="Eg. 1234ABCD33DD"
                variant="outlined"
                id="pat_info.otherId"
                name="pat_info.otherId"
                sx={{
                  paddingLeft: "3px",
                }}
                onBlur={handlePatientIdBlur}
                onChange={formik.handleChange}
                value={formik.values.pat_info.otherId}
                error={formik.touched.pat_info?.otherId && Boolean(formik.errors.pat_info?.otherId)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === "Tab") {
                    searchPatient();
                  }
                }}
                disabled={isFollowUp}
              />
              {formik.errors.pat_info?.otherId && (
                <Typography color="error">{formik.errors.pat_info.otherId}</Typography>
              )}
            </StyledGridItem>

            {/* Gender */}
            <StyledGridItem item xs={12}>
              <StyledLabel>Gender*</StyledLabel>
              <StyledRadioGroup
                row={!isMobile} // Keep radio buttons in a row for desktop
                name="pat_info.gender"
                value={formik.values.pat_info.gender}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              >
                {genderArray.map((gender) => {
                  const isSelected = formik.values.pat_info.gender === gender.toLowerCase();
                  return (
                    <StyledFormControlLabel
                      key={gender}
                      value={gender.toLowerCase()}
                      control={
                        <StyledRadio
                          checked={isSelected}
                        />
                      }
                      label={gender}
                      disabled={isFollowUp}
                      checked={isSelected}
                    />
                  );
                })}
              </StyledRadioGroup>
              {formik.touched.pat_info?.gender && formik.errors.pat_info?.gender && (
                <Typography color="error">
                  {formik.errors.pat_info.gender}
                </Typography>
              )}
            </StyledGridItem>

            {/* Age */}
            <StyledGridItem item xs={12}>
              <StyledLabel>Age*</StyledLabel>
              <Box sx={{ maxWidth: "100px" }}>
                <StyledTextField
                  required
                  type="number"
                  placeholder="XX"
                  variant="outlined"
                  id="pat_info.age"
                  name="pat_info.age"
                  sx={{
                    paddingLeft: "3px",
                  }}
                  onBlur={formik.handleBlur}
                  onChange={formik.handleChange}
                  value={formik.values.pat_info.age}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">Years</InputAdornment>,
                  }}
                  error={
                    formik.touched.pat_info?.age &&
                    Boolean(formik.errors.pat_info?.age)
                  }
                />
              </Box>
              {formik.touched.pat_info?.age && formik.errors.pat_info?.age && (
                <Typography color="error">
                  {formik.errors.pat_info.age}
                </Typography>
              )}
            </StyledGridItem>

            {/* Diabetes */}
            <StyledGridItem item xs={12}>
              <StyledLabel>Diabetes*</StyledLabel>
              <StyledRadioGroup
                row={!isMobile} // Keep radio buttons in a row for desktop
                name="pat_info.diabetes"
                value={formik.values.pat_info.diabetes ?? ""}
                onChange={formik.handleChange}
              >
                {["Yes", "No", "Unknown"].map((option) => {
                  const isSelected = formik.values.pat_info.diabetes === option;
                  return (
                    <StyledFormControlLabel
                      key={option}
                      value={option}
                      control={<StyledRadio checked={isSelected} />}
                      label={option}
                      checked={isSelected}
                    />
                  );
                })}
              </StyledRadioGroup>
              {formik.touched.pat_info?.diabetes && formik.errors.pat_info?.diabetes && (
                <Typography color="error">
                  {formik.errors.pat_info.diabetes}
                </Typography>
              )}
            </StyledGridItem>

            {/* Duration Since Diabetes Diagnosed - Conditional */}
            {formik.values.pat_info.diabetes === "Yes" && (
              <StyledGridItem item xs={12}>
                <StyledLabel>
                  {isMobile ? "Duration Since Diabetes Diagnosed" : (
                    <>Duration Since<br />Diabetes Diagnosed</>
                  )}
                </StyledLabel>
                <Box sx={{ 
                  display: "flex", 
                  flexDirection: isMobile ? "row" : "row", // Keep in one row on mobile
                  gap: 2, 
                  alignItems: isMobile ? "flex-start" : "flex-start",
                  width: isMobile ? "100%" : "auto",
                  flexWrap: isMobile ? "nowrap" : "wrap", // No wrap on mobile to keep in one line
                  marginLeft: isMobile ? 0 : 0 // Ensure alignment starts from same point
                }}>
                  <Box sx={{ 
                    display: "flex",
                    flexDirection: "column",
                    maxWidth: isMobile ? "48%" : "140px", 
                    width: isMobile ? "48%" : "140px",
                    
                    flexShrink: 0
                  }}>
                    <StyledTextField
                      type="number"
                      placeholder="XX"
                      variant="outlined"
                      id="pat_info.diabetesDurationYears"
                      name="pat_info.diabetesDurationYears"
                      value={formik.values.pat_info.diabetesDurationYears ?? ""}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      InputProps={{
                        endAdornment: <InputAdornment position="end">Years</InputAdornment>,
                      }}
                      error={
                        formik.touched.pat_info?.diabetesDurationYears &&
                        Boolean(formik.errors.pat_info?.diabetesDurationYears)
                      }
                      helperText={
                        formik.touched.pat_info?.diabetesDurationYears && formik.errors.pat_info?.diabetesDurationYears
                          ? formik.errors.pat_info.diabetesDurationYears
                          : formik.touched.pat_info?.diabetesDurationMonths && formik.errors.pat_info?.diabetesDurationMonths && 
                            !formik.values.pat_info.diabetesDurationYears && 
                            !formik.values.pat_info.diabetesDurationMonths
                          ? "" // Don't show error on years if months already shows it and both are empty
                          : null
                      }
                      FormHelperTextProps={{
                        sx: { margin: 0 }
                      }}
                      sx={{
                        paddingLeft: "3px",
                        width: "100%",
                      }}
                    />
                  </Box>
                  <Box sx={{ 
                    display: "flex",
                    flexDirection: "column",
                    maxWidth: isMobile ? "48%" : "140px", 
                    width: isMobile ? "48%" : "140px",
                    
                    flexShrink: 0
                  }}>
                    <StyledTextField
                      type="number"
                      placeholder="XX"
                      variant="outlined"
                      id="pat_info.diabetesDurationMonths"
                      name="pat_info.diabetesDurationMonths"
                      value={formik.values.pat_info.diabetesDurationMonths ?? ""}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      InputProps={{
                        endAdornment: <InputAdornment position="end">Months</InputAdornment>,
                      }}
                      error={
                        formik.touched.pat_info?.diabetesDurationMonths &&
                        Boolean(formik.errors.pat_info?.diabetesDurationMonths)
                      }
                      helperText={
                        formik.touched.pat_info?.diabetesDurationMonths && formik.errors.pat_info?.diabetesDurationMonths
                          ? formik.errors.pat_info.diabetesDurationMonths
                          : null
                      }
                      FormHelperTextProps={{
                        sx: { margin: 0 }
                      }}
                      sx={{
                        paddingLeft: "3px",
                        width: "100%",
                      }}
                    />
                  </Box>
                </Box>
              </StyledGridItem>
            )}
            {/* Best Corrected Visual Acuity */}
            {/* Best Corrected Visual Acuity */}
            <StyledGridItem item xs={12}>
              <StyledLabel>
                {isMobile ? "Best Corrected Visual Acuity" : (
                  <>Best Corrected<br />Visual Acuity</>
                )}
              </StyledLabel>
              <Box sx={{ 
                display: "flex", 
                flexDirection: "column",
                gap: 2,
                width: isMobile ? "100%" : "auto",
              }}>
                {/* Distant Vision Section */}
                <Box sx={{ 
                  display: "flex",
                  flexDirection: "column",
                  gap: 1
                }}>
                  <Typography sx={{ fontSize: "14px", fontWeight: 500, marginBottom: "8px" }}>
                    Distant Vision
                  </Typography>
                  <Box sx={{ 
                    display: "flex", 
                    flexDirection: isMobile ? "row" : "row",
                    gap: isMobile ? 1 : 2,
                    alignItems: "flex-start",
                    width: isMobile ? "100%" : "auto",
                  }}>
                    <Box sx={{ 
                      display: "flex",
                      flexDirection: "column",
                      width: isMobile ? "48%" : "140px",
                      minWidth: isMobile ? "120px" : "auto",
                      flexShrink: 0
                    }}>
                      <Autocomplete
                        freeSolo
                        disableClearable
                        options={distantVisionOptions}
                        filterOptions={filterDistantVisionOptions}
                        value={formik.values.pat_info.visualAcuity?.distant?.right ?? null}
                        onChange={(event, newValue) => {
                          const trimmedValue = newValue ? String(newValue).trim() : '';
                          formik.setFieldValue('pat_info.visualAcuity.distant.right', trimmedValue);
                          formik.setFieldTouched('pat_info.visualAcuity.distant.right', true);
                          // Validate after setting the value
                          setTimeout(() => {
                            formik.validateField('pat_info.visualAcuity.distant.right');
                          }, 0);
                        }}
                        onInputChange={(event, newInputValue, reason) => {
                          if (reason === 'input') {
                            formik.setFieldValue('pat_info.visualAcuity.distant.right', newInputValue || '');
                            formik.setFieldTouched('pat_info.visualAcuity.distant.right', true);
                          }
                        }}
                        onBlur={() => {
                          formik.setFieldTouched('pat_info.visualAcuity.distant.right', true);
                          formik.validateField('pat_info.visualAcuity.distant.right');
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            placeholder="__/__"
                            variant="outlined"
                            error={
                              formik.touched.pat_info?.visualAcuity?.distant?.right &&
                              Boolean(formik.errors.pat_info?.visualAcuity?.distant?.right)
                            }
                            helperText={
                              formik.touched.pat_info?.visualAcuity?.distant?.right && formik.errors.pat_info?.visualAcuity?.distant?.right
                                ? formik.errors.pat_info.visualAcuity.distant.right
                                : null
                            }
                            FormHelperTextProps={{
                              sx: { margin: 0 }
                            }}
                            InputProps={{
                              ...params.InputProps,
                              endAdornment: (
                                <>
                                  {params.InputProps.endAdornment}
                                  <InputAdornment position="end" sx={{ marginLeft: "8px" }}>Right</InputAdornment>
                                </>
                              ),
                            }}
                            sx={{
                              paddingLeft: "3px",
                              width: "100%",
                            }}
                          />
                        )}
                      />
                    </Box>
                    <Box sx={{ 
                      display: "flex",
                      flexDirection: "column",
                      width: isMobile ? "48%" : "140px",
                      minWidth: isMobile ? "120px" : "auto",
                      flexShrink: 0
                    }}>
                      <Autocomplete
                        freeSolo
                        disableClearable
                        options={distantVisionOptions}
                        filterOptions={filterDistantVisionOptions}
                        value={formik.values.pat_info.visualAcuity?.distant?.left ?? null}
                        onChange={(event, newValue) => {
                          const trimmedValue = newValue ? String(newValue).trim() : '';
                          formik.setFieldValue('pat_info.visualAcuity.distant.left', trimmedValue);
                          formik.setFieldTouched('pat_info.visualAcuity.distant.left', true);
                          // Validate after setting the value
                          setTimeout(() => {
                            formik.validateField('pat_info.visualAcuity.distant.left');
                          }, 0);
                        }}
                        onInputChange={(event, newInputValue, reason) => {
                          if (reason === 'input') {
                            // User is typing - set the value and mark as touched
                            formik.setFieldValue('pat_info.visualAcuity.distant.left', newInputValue || '');
                            formik.setFieldTouched('pat_info.visualAcuity.distant.left', true);
                          }
                        }}
                        onBlur={() => {
                          formik.setFieldTouched('pat_info.visualAcuity.distant.left', true);
                          formik.validateField('pat_info.visualAcuity.distant.left');
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            placeholder="__/__"
                            variant="outlined"
                            error={
                              formik.touched.pat_info?.visualAcuity?.distant?.left &&
                              Boolean(formik.errors.pat_info?.visualAcuity?.distant?.left)
                            }
                            helperText={
                              formik.touched.pat_info?.visualAcuity?.distant?.left && formik.errors.pat_info?.visualAcuity?.distant?.left
                                ? formik.errors.pat_info.visualAcuity.distant.left
                                : null
                            }
                            FormHelperTextProps={{
                              sx: { margin: 0 }
                            }}
                            InputProps={{
                              ...params.InputProps,
                              endAdornment: (
                                <>
                                  {params.InputProps.endAdornment}
                                  <InputAdornment position="end" sx={{ marginLeft: "8px" }}>Left</InputAdornment>
                                </>
                              ),
                            }}
                            sx={{
                              paddingLeft: "3px",
                              width: "100%",
                            }}
                          />
                        )}
                      />
                    </Box>
                  </Box>
                </Box>

                {/* Near Vision Section */}
                <Box sx={{ 
                  display: "flex",
                  flexDirection: "column",
                  gap: 1
                }}>
                  <Typography sx={{ fontSize: "14px", fontWeight: 500, marginBottom: "8px" }}>
                    Near Vision
                  </Typography>
                  <Box sx={{ 
                    display: "flex", 
                    flexDirection: isMobile ? "row" : "row",
                    gap: isMobile ? 1 : 2,
                    alignItems: "flex-start",
                    width: isMobile ? "100%" : "auto",
                  }}>
                    <Box sx={{ 
                      display: "flex",
                      flexDirection: "column",
                      width: isMobile ? "48%" : "140px",
                      minWidth: isMobile ? "120px" : "auto",
                      flexShrink: 0
                    }}>
                      <Autocomplete
                        freeSolo
                        disableClearable
                        options={nearVisionOptions}
                        filterOptions={filterNearVisionOptions}
                        value={formik.values.pat_info.visualAcuity?.near?.right ?? null}
                        onChange={(event, newValue) => {
                          const trimmedValue = newValue ? String(newValue).trim() : '';
                          formik.setFieldValue('pat_info.visualAcuity.near.right', trimmedValue);
                          formik.setFieldTouched('pat_info.visualAcuity.near.right', true);
                          // Validate after setting the value
                          setTimeout(() => {
                            formik.validateField('pat_info.visualAcuity.near.right');
                          }, 0);
                        }}
                        onInputChange={(event, newInputValue, reason) => {
                          if (reason === 'input') {
                            formik.setFieldValue('pat_info.visualAcuity.near.right', newInputValue || '');
                            formik.setFieldTouched('pat_info.visualAcuity.near.right', true);
                          }
                        }}
                        onBlur={() => {
                          formik.setFieldTouched('pat_info.visualAcuity.near.right', true);
                          formik.validateField('pat_info.visualAcuity.near.right');
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            placeholder="N__"
                            variant="outlined"
                            error={
                              formik.touched.pat_info?.visualAcuity?.near?.right &&
                              Boolean(formik.errors.pat_info?.visualAcuity?.near?.right)
                            }
                            helperText={
                              formik.touched.pat_info?.visualAcuity?.near?.right && formik.errors.pat_info?.visualAcuity?.near?.right
                                ? formik.errors.pat_info.visualAcuity.near.right
                                : null
                            }
                            FormHelperTextProps={{
                              sx: { margin: 0 }
                            }}
                            InputProps={{
                              ...params.InputProps,
                              endAdornment: (
                                <>
                                  {params.InputProps.endAdornment}
                                  <InputAdornment position="end" sx={{ marginLeft: "8px" }}>Right</InputAdornment>
                                </>
                              ),
                            }}
                            sx={{
                              paddingLeft: "3px",
                              width: "100%",
                            }}
                          />
                        )}
                      />
                    </Box>
                    <Box sx={{ 
                      display: "flex",
                      flexDirection: "column",
                      width: isMobile ? "48%" : "140px",
                      minWidth: isMobile ? "120px" : "auto",
                      flexShrink: 0
                    }}>
                      <Autocomplete
                        freeSolo
                        disableClearable
                        options={nearVisionOptions}
                        filterOptions={filterNearVisionOptions}
                        value={formik.values.pat_info.visualAcuity?.near?.left ?? null}
                        onChange={(event, newValue) => {
                          const trimmedValue = newValue ? String(newValue).trim() : '';
                          formik.setFieldValue('pat_info.visualAcuity.near.left', trimmedValue);
                          formik.setFieldTouched('pat_info.visualAcuity.near.left', true);
                          // Validate after setting the value
                          setTimeout(() => {
                            formik.validateField('pat_info.visualAcuity.near.left');
                          }, 0);
                        }}
                        onInputChange={(event, newInputValue, reason) => {
                          if (reason === 'input') {
                            formik.setFieldValue('pat_info.visualAcuity.near.left', newInputValue || '');
                            formik.setFieldTouched('pat_info.visualAcuity.near.left', true);
                          }
                        }}
                        onBlur={() => {
                          formik.setFieldTouched('pat_info.visualAcuity.near.left', true);
                          formik.validateField('pat_info.visualAcuity.near.left');
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            placeholder="N__"
                            variant="outlined"
                            error={
                              formik.touched.pat_info?.visualAcuity?.near?.left &&
                              Boolean(formik.errors.pat_info?.visualAcuity?.near?.left)
                            }
                            helperText={
                              formik.touched.pat_info?.visualAcuity?.near?.left && formik.errors.pat_info?.visualAcuity?.near?.left
                                ? formik.errors.pat_info.visualAcuity.near.left
                                : null
                            }
                            FormHelperTextProps={{
                              sx: { margin: 0 }
                            }}
                            InputProps={{
                              ...params.InputProps,
                              endAdornment: (
                                <>
                                  {params.InputProps.endAdornment}
                                  <InputAdornment position="end" sx={{ marginLeft: "8px" }}>Left</InputAdornment>
                                </>
                              ),
                            }}
                            sx={{
                              paddingLeft: "3px",
                              width: "100%",
                            }}
                          />
                        )}
                      />
                    </Box>
                  </Box>
                </Box>
              </Box>
            </StyledGridItem>

          </>
        )}
      </Grid>
    </StyledContainer>
  );
};

export default NewPatientForm;

